// Default storefront layout: skip link + variant-resolved Header + main
// content slot + variant-resolved Footer + (mobile-only) sticky cart bar.
import { useEffect } from 'react';
import { Outlet, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useStoreClosed } from '@core/utils/storeStatus';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import { useStoreContextStore } from '@core/store/storeContextStore';
import { useToast } from '@core/hooks/useToast';
import Header from '@components/variants/Header';
import Footer from '@components/variants/Footer';
import MobileCartBar from '@components/variants/MobileCartBar';
import MobileTabBar from '@components/variants/MobileTabBar';
import { ProductDetailModal } from '@features/product-detail/ProductDetailView.jsx';
import FlyToCartOverlay from '@components/base/FlyToCartOverlay/FlyToCartOverlay.jsx';
import {
  CHECKOUT_MODAL_PARAM,
  openCheckoutModal,
} from '@components/base/CheckoutModal/checkoutModalEvents.js';
import { useCopy } from '@/app/providers/useCopy';

export default function DefaultLayout() {
  const { isClosed, storeStatus } = useStoreClosed();
  const accountSelectorOpen = useAccountSelectionStore((s) => s.accountSelectorOpen);
  const locationId = useStoreContextStore((s) => s.locationId);
  const t = useCopy();
  const toast = useToast();

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const match = location.pathname.match(/\/products\/([^/]+)/);
  const pathProductId = match ? match[1] : null;
  const queryProductId = searchParams.get('product');
  const activeProductId = pathProductId || queryProductId;
  const mode = searchParams.get('mode') || null;

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('product-detail-modal-change', {
        detail: { open: Boolean(activeProductId) },
      }),
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent('product-detail-modal-change', {
          detail: { open: false },
        }),
      );
    };
  }, [activeProductId]);

  const handleClose = () => {
    if (pathProductId) {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate('/', { replace: true });
      }
    } else {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('product');
          next.delete('mode');
          return next;
        },
        { replace: true },
      );
    }
  };

  // `?checkout=open` (e.g. after a cancelled gateway payment) reopens the
  // checkout modal. The header mounts first, so its listener is ready.
  const checkoutParam = searchParams.get(CHECKOUT_MODAL_PARAM);
  useEffect(() => {
    if (checkoutParam !== 'open') return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CHECKOUT_MODAL_PARAM);
        return next;
      },
      { replace: true },
    );
    openCheckoutModal();
  }, [checkoutParam, setSearchParams]);

  useEffect(() => {
    const msg = sessionStorage.getItem('bytz.toast_message');
    if (msg) {
      toast.success(msg);
      sessionStorage.removeItem('bytz.toast_message');
    }
  }, [toast]);

  const closedMsg = t('weAreCurrentlyClosed', '⚠️ We are currently closed.');
  const nextOpenMsg = t('nextOpenTime', 'Next open time');
  const message =
    storeStatus && storeStatus.nextOpenTime
      ? `${closedMsg} ${nextOpenMsg}: ${storeStatus.nextOpenTime}`
      : closedMsg;

  return (
    <div
      className="app-shell"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-page)',
      }}
    >
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      {isClosed && !accountSelectorOpen && locationId && (
        <div className="store-closed-marquee-banner" role="alert" aria-live="polite">
          <div className="marquee-track">
            <span className="marquee-item">{message}</span>
            <span className="marquee-item">{message}</span>
            <span className="marquee-item">{message}</span>
          </div>
        </div>
      )}
      <Header />
      <div id="main" style={{ flex: 1, paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <Outlet />
      </div>
      <Footer />
      <MobileCartBar />
      <MobileTabBar />
      <ProductDetailModal
        open={Boolean(activeProductId)}
        onCancel={handleClose}
        productId={activeProductId}
        initialMode={mode}
        onDone={handleClose}
      />
      <FlyToCartOverlay />
    </div>
  );
}
